import { Centrifuge, Subscription, PublicationContext } from 'centrifuge';
import type { CevroMessengerConfig, WSEventPayload } from '../types';
import { EventEmitter } from './EventEmitter';

/**
 * Token refresh callback type
 */
export type TokenRefreshCallback = () => Promise<string>;

/**
 * WebSocket client for real-time messaging via Centrifugo
 */
export class WebSocketClient {
  private config: CevroMessengerConfig;
  private events: EventEmitter;
  private client: Centrifuge | null = null;
  private personalSub: Subscription | null = null;
  private userId: string | null = null;
  private getTokenCallback: TokenRefreshCallback | null = null;

  constructor(config: CevroMessengerConfig, events: EventEmitter) {
    this.config = config;
    this.events = events;
  }

  /**
   * Update config reference (used when config object is replaced)
   */
  updateConfig(config: CevroMessengerConfig): void {
    this.config = config;
  }

  /**
   * Set token refresh callback
   * Called when Centrifugo needs a new token (expiration, reconnect)
   */
  setTokenRefreshCallback(callback: TokenRefreshCallback): void {
    this.getTokenCallback = callback;
  }

  /**
   * Connect to Centrifugo server
   */
  connect(userId: string, connectionToken: string): void {
    if (this.client?.state === 'connected') {
      return;
    }

    this.userId = userId;

    // Determine WebSocket URL from API base
    const wsUrl = this.getWebSocketUrl();

    // Build Centrifuge options
    const options: any = {
      token: connectionToken,
    };

    // Add token refresh callback if available
    // This is called when token expires or on reconnect
    if (this.getTokenCallback) {
      options.getToken = async () => {
        return await this.getTokenCallback!();
      };
    }

    this.client = new Centrifuge(wsUrl, options);

    this.setupClientEventListeners();

    // Connect
    this.client.connect();
  }

  /**
   * Get WebSocket URL from config
   */
  private getWebSocketUrl(): string {
    // If centrifugoUrl is explicitly set, use it
    if (this.config.centrifugoUrl) {
      return this.config.centrifugoUrl;
    }

    // Otherwise derive from apiBase
    // e.g., https://api.example.com -> wss://ws.example.com/connection/websocket
    const apiUrl = new URL(this.config.apiBase);
    const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';

    // Default to same host with /connection/websocket path
    return `${wsProtocol}//${apiUrl.host}/connection/websocket`;
  }

  /**
   * Setup Centrifuge client event listeners
   */
  private setupClientEventListeners(): void {
    if (!this.client) return;

    this.client.on('connected', (ctx) => {
      this.events.emit('connection:open', { clientId: ctx.client });
      this.subscribeToPersonalChannel();
    });

    this.client.on('disconnected', (ctx) => {
      this.events.emit('connection:close', { reason: ctx.reason, code: ctx.code });
    });

    this.client.on('error', (ctx) => {
      this.events.emit('connection:error', { error: ctx.error?.message || 'Unknown error' });
    });
  }

  /**
   * Subscribe to visitor channel for receiving messages
   * Uses visitorId which is always present (even for authenticated players)
   */
  private subscribeToPersonalChannel(): void {
    if (!this.client || !this.userId) return;

    // Always use visitor channel - visitorId is always present
    const channelName = `visitor:${this.userId}`;

    // Check if already subscribed (handles reconnection case)
    const existingSub = this.client.getSubscription(channelName);
    if (existingSub) {
      this.personalSub = existingSub;
      if (existingSub.state !== 'subscribed') {
        existingSub.subscribe();
      }
      return;
    }

    this.personalSub = this.client.newSubscription(channelName);

    this.personalSub.on('publication', (ctx: PublicationContext) => {
      this.handleMessage(ctx.data as WSEventPayload);
    });

    this.personalSub.on('error', (ctx) => {
      this.events.emit('error', { type: 'subscription', error: ctx.error?.message });
    });

    this.personalSub.subscribe();
  }

  /**
   * Handle incoming message from Centrifugo
   * Payload structure: { type, data: {...}, timestamp }
   */
  private handleMessage(payload: any): void {
    const data = payload.data || payload;

    switch (payload.type) {
      case 'new_message':
        // Convert backend format to Message format
        const message = {
          id: data.id,
          body: data.body,
          inbound: data.inbound || false,
          outbound: data.outbound || false,
          createdAt: data.createdAt,
          type: data.type || 'text',
          mediaUrl: data.mediaUrl,
          senderType: data.senderType,
          agentName: data.agentName,
        };
        this.events.emit('message:received', {
          ticketId: data.ticketId,
          message,
        });
        break;

      case 'typing':
        if (data.isTyping) {
          this.events.emit('typing:start', {
            ticketId: data.ticketId,
            actor: data.senderType || 'agent',
          });
        } else {
          this.events.emit('typing:stop', {
            ticketId: data.ticketId,
            actor: data.senderType || 'agent',
          });
        }
        break;

      case 'ticket_created':
        this.events.emit('conversation:started', {
          ticketId: data.ticketId,
          contactId: data.contactId,
          createdAt: data.createdAt,
        });
        break;

      case 'ticket_status':
        this.events.emit('conversation:updated', {
          ticketId: data.ticketId,
          status: data.status,
          message: data.message,
          closedAt: data.closedAt,
          agentName: data.agentName,
        });
        break;
    }
  }

  /**
   * Update connection token (for token refresh)
   */
  updateToken(newToken: string): void {
    if (this.client) {
      this.client.setToken(newToken);
    }
  }

  /**
   * Disconnect from Centrifugo
   */
  disconnect(): void {
    if (this.personalSub) {
      this.personalSub.unsubscribe();
      this.personalSub = null;
    }

    if (this.client) {
      this.client.disconnect();
      this.client = null;
    }

    this.userId = null;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.client?.state === 'connected';
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.userId;
  }
}
